import { useState } from 'react';
import { escapeCSV, parseCSV, toYayoiDate, fromYayoiDate } from '../utils/csv';
import { getCurrentFiscalYear } from '../utils/date';
import {
    ACCOUNT_TYPES,
    TYPE_MAP,
    TYPE_MAP_REVERSE,
    HOME_MAP,
    HOME_MAP_REVERSE,
    STATEMENT_MAP,
    STATEMENT_MAP_REVERSE,
    inferAccountType
} from '../constants/accountTypes';
import { formatTaxString, parseTaxString } from '../constants/taxTypes';
import { DEFAULT_ACCOUNTS, generateAccountId } from '../constants/defaultAccounts';

const API_URL = import.meta.env.VITE_API_URL;
// accountTypeをグローバル変数として保持（localStorageから初期化、orgInfo読み込み後に更新）
let cachedAccountType = (typeof localStorage !== 'undefined' && localStorage.getItem('haniho_account_type')) || 'houjin';
export const setAccountType = (type) => { cachedAccountType = type; };
const getApiType = () => cachedAccountType;

// 仕訳履歴を保存
export const saveJournalHistory = async (userEmail, year, endMonth, historyEntry) => {
    try {
        const response = await fetch(`${API_URL}?action=saveJournalHistory&type=${getApiType()}&year=${year}&endMonth=${endMonth}&user=${encodeURIComponent(userEmail)}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ history: historyEntry })
        });
        const result = await response.json();
        return result;
    } catch (err) {
        console.error('Failed to save journal history:', err);
        return { success: false, error: err.message };
    }
};

// 仕訳履歴を取得
export const getJournalHistory = async (userEmail, year, endMonth, journalId = null) => {
    try {
        let url = `${API_URL}?action=getJournalHistory&type=${getApiType()}&year=${year}&endMonth=${endMonth}&user=${encodeURIComponent(userEmail)}`;
        if (journalId) {
            url += `&journalId=${encodeURIComponent(journalId)}`;
        }
        const response = await fetch(url);
        const result = await response.json();
        return result.history || [];
    } catch (err) {
        console.error('Failed to get journal history:', err);
        return [];
    }
};

// 次の伝票番号を取得
export const getNextEntryNumber = async (userEmail, year) => {
    try {
        const response = await fetch(`${API_URL}?action=getNextEntryNumber&type=${getApiType()}&year=${year}&user=${encodeURIComponent(userEmail)}`);
        const result = await response.json();
        return result.entryNumber || null;
    } catch (err) {
        console.error('Failed to get next entry number:', err);
        return null;
    }
};

export const useFileSystem = (userEmail) => {
    const [isConnected, setIsConnected] = useState(true);
    const [basePath, setBasePath] = useState('Google Drive');

    const selectBaseDirectory = async () => {
        return true;
    };

    const getSettings = async () => {
        if (!userEmail) {
            console.warn('User email is required to load settings');
            return null;
        }
        try {
            const response = await fetch(`${API_URL}?action=getSettings&type=${getApiType()}&user=${encodeURIComponent(userEmail)}`);
            if (!response.ok) {
                if (response.status === 404) {
                    // 設定ファイルが存在しない場合はデフォルト値を返す
                    return {
                        detailSettings: {
                            bankAccounts: [],
                            partners: [],
                            officers: [],
                            rentals: []
                        },
                        allocationSettings: {
                            method: 'revenue',
                            revenueRatio: 50,
                            nonRevenueRatio: 50,
                            customRatios: {}
                        }
                    };
                }
                throw new Error('Failed to fetch settings');
            }
            const data = await response.json();
            return data.settings || null;
        } catch (err) {
            console.error('Failed to load settings:', err);
            return null;
        }
    };

    const saveSettings = async (settings) => {
        if (!userEmail) {
            console.warn('User email is required to save settings');
            return false;
        }
        try {
            const response = await fetch(
                `${API_URL}?action=saveSettings&type=${getApiType()}&user=${encodeURIComponent(userEmail)}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ settings })
                }
            );
            const result = await response.json();
            return result.success;
        } catch (err) {
            console.error('Failed to save settings:', err);
            return false;
        }
    };

    const getYearDirectory = async (year) => {
        return true;
    };

    const journalsToCSV = (journals, accounts, options = {}) => {
        const { includeDeletedMeta = false } = options;
        const lines = [];
        const headers = [
            '識別フラグ', '伝票No', '決算', '取引日付',
            '借方科目', '借方補助科目', '借方部門', '借方税区分', '借方金額', '借方税額',
            '貸方科目', '貸方補助科目', '貸方部門', '貸方税区分', '貸方金額', '貸方税額',
            '摘要', '番号', '期日', 'タイプ', '仕訳ID', '証憑', '証憑URL', '証憑サムネイル', '区分', '経費区分',
            '借方T番号', '借方取引先名', '借方備考', '貸方T番号', '貸方取引先名', '貸方備考',
            '入力日時', '貸方カードID'
        ];
        if (includeDeletedMeta) {
            headers.push('削除日時', '削除者メール', '削除者名');
        }
        lines.push(headers.map(escapeCSV).join(','));

        let voucherNo = 1;
        journals.forEach(journal => {
            const debitLines = journal.lines.filter(l => l.type === 'debit');
            const creditLines = journal.lines.filter(l => l.type === 'credit');

            if (debitLines.length === 1 && creditLines.length === 1) {
                const debit = debitLines[0];
                const credit = creditLines[0];
                const debitAcc = accounts.find(a => a.id === debit.accountId);
                const creditAcc = accounts.find(a => a.id === credit.accountId);

                const row = [
                    '2000', voucherNo, '', toYayoiDate(journal.date),
                    debitAcc?.name || '', '', '', formatTaxString(debit.taxType, debit.taxRate), debit.amount, '',
                    creditAcc?.name || '', '', '', formatTaxString(credit.taxType, credit.taxRate), credit.amount, '',
                    journal.description, '', '', '0', journal.id,
                    journal.voucher || '', journal.voucherUrl || '', journal.voucherThumb || '', journal.voucherId || '',
                    journal.category || '', journal.expenseCategory || '',
                    debit.invoiceNumber || '', debit.vendorName || '', debit.vendorMemo || '',
                    credit.invoiceNumber || '', credit.vendorName || '', credit.vendorMemo || '',
                    journal.createdAt || '', credit.creditCardId || ''
                ];
                if (includeDeletedMeta) {
                    row.push(journal.deletedAt || '', journal.deletedBy || '', journal.deletedByName || '');
                }
                lines.push(row.map(escapeCSV).join(','));
            } else {
                const maxLen = Math.max(debitLines.length, creditLines.length);
                for (let i = 0; i < maxLen; i++) {
                    const debit = debitLines[i];
                    const credit = creditLines[i];
                    const debitAcc = debit ? accounts.find(a => a.id === debit.accountId) : null;
                    const creditAcc = credit ? accounts.find(a => a.id === credit.accountId) : null;

                    const row = [
                        '2000', voucherNo, '', toYayoiDate(journal.date),
                        debitAcc?.name || '諸口', '', '', debit ? formatTaxString(debit.taxType, debit.taxRate) : '対象外', debit?.amount || '', '',
                        creditAcc?.name || '諸口', '', '', credit ? formatTaxString(credit.taxType, credit.taxRate) : '対象外', credit?.amount || '', '',
                        journal.description, '', '', '0', journal.id,
                        i === 0 ? (journal.voucher || '') : '',
                        i === 0 ? (journal.voucherUrl || '') : '',
                        i === 0 ? (journal.voucherThumb || '') : '',
                        i === 0 ? (journal.voucherId || '') : '',
                        i === 0 ? (journal.category || '') : '',
                        i === 0 ? (journal.expenseCategory || '') : '',
                        debit?.invoiceNumber || '', debit?.vendorName || '', debit?.vendorMemo || '',
                        credit?.invoiceNumber || '', credit?.vendorName || '', credit?.vendorMemo || '',
                        i === 0 ? (journal.createdAt || '') : '',
                        credit?.creditCardId || ''
                    ];
                    if (includeDeletedMeta && i === 0) {
                        row.push(journal.deletedAt || '', journal.deletedBy || '', journal.deletedByName || '');
                    } else if (includeDeletedMeta) {
                        row.push('', '', '');
                    }
                    lines.push(row.map(escapeCSV).join(','));
                }
            }
            voucherNo++;
        });

        return lines.join('\n');
    };

    const csvToJournals = (csvText, accounts, options = {}) => {
        const rows = parseCSV(csvText);
        console.log('csvToJournals called, rows:', rows.length);
        console.log('first row:', JSON.stringify(rows[0]));
        if (rows.length < 1) return { journals: [], newAccounts: [], skippedCount: 0 };

        const { existingJournals = [] } = options;
        const existingImportKeys = new Set(
            existingJournals.filter(j => j.importKey).map(j => j.importKey)
        );

        const journalMap = new Map();
        const newAccountsMap = new Map();
        let workingAccounts = [...accounts];
        let nextCode = Math.max(...accounts.map(a => parseInt(a.code) || 0), 9000) + 1;
        let skippedCount = 0;

        const getOrCreateAccount = (name, code = null) => {
            if (!name) return null;
            if (name === '諸口') return { id: null, name: '諸口' };

            // 1. コードでマッチング（コードがある場合）
            let acc = code ? workingAccounts.find(a => a.code === code) : null;

            // 2. コードで見つからなければ名前でマッチング
            if (!acc) {
                acc = workingAccounts.find(a => a.name === name);
            }

            // 3. どちらも見つからなければ新規作成
            const cacheKey = `${name}_${code || ''}`;
            if (!acc && !newAccountsMap.has(cacheKey)) {
                // 科目名から勘定科目種別を推定
                const newCode = code || String(nextCode);
                const tempAccount = { name: name, code: newCode, statement: 'PL' };
                const inferredType = inferAccountType(tempAccount);
                // 推定結果に基づいてstatementとhomeを設定
                const isBS = [ACCOUNT_TYPES.ASSET, ACCOUNT_TYPES.LIABILITY, ACCOUNT_TYPES.EQUITY].includes(inferredType);
                const isDebit = [ACCOUNT_TYPES.ASSET, ACCOUNT_TYPES.EXPENSE].includes(inferredType);
                // 統一フォーマットでID生成: acc_{code}_{name}
                acc = {
                    id: generateAccountId(newCode, name),
                    code: newCode,
                    name: name,
                    kana: '',
                    type: inferredType,
                    home: isDebit ? 'debit' : 'credit',
                    statement: isBS ? 'BS' : 'PL',
                    counterpart: '',
                    description: '【要編集】インポートで自動追加された科目',
                    isNew: true
                };
                workingAccounts.push(acc);
                newAccountsMap.set(cacheKey, acc);
                if (!code) nextCode++;
            } else if (newAccountsMap.has(cacheKey)) {
                acc = newAccountsMap.get(cacheKey);
            }
            return acc;
        };

        const detectFormat = (row) => {
            const col6 = row[6] || '';
            const col7 = row[7] || '';
            const isTaxString = (s) => s.includes('課') || s.includes('対象外') || s.includes('非課税') || s.includes('免税');

            if (isTaxString(col6)) {
                return 'jobcan';
            }
            if (col6 === '' && isTaxString(col7)) {
                return 'yayoi';
            }
            if (col6 === '' && (col7 === '' || !isNaN(col7))) {
                return 'jobcan';
            }
            return 'yayoi';
        };

        const hasHeader = rows.length > 0 && rows[0][0] !== '2000';
        const startIndex = hasHeader ? 1 : 0;
        const hasCreditCardColumn = hasHeader && rows[0].includes('貸方カードID');

        const firstDataRow = rows.find((r, i) => i >= startIndex && r[0] === '2000');
        const csvFormat = firstDataRow ? detectFormat(firstDataRow) : 'yayoi';

        for (let i = startIndex; i < rows.length; i++) {
            const row = rows[i];
            if (row.length < 15) continue;

            let flag, voucherNo, settlement, dateStr,
                debitAccName, debitSub, debitTax, debitAmount, debitTaxAmt,
                creditAccName, creditSub, creditTax, creditAmount, creditTaxAmt,
                description, number, dueDate, type, journalId,
                voucher, voucherUrl, voucherThumb, voucherId,
                category, expenseCategory,
                debitInvoiceNumber, debitVendorName, debitVendorMemo,
                creditInvoiceNumber, creditVendorName, creditVendorMemo,
                createdAt, creditCardId,
                deletedAt, deletedBy, deletedByName;

            if (csvFormat === 'jobcan') {
                [
                    flag, voucherNo, settlement, dateStr,
                    debitAccName, debitSub, debitTax, debitAmount, debitTaxAmt,
                    creditAccName, creditSub, creditTax, creditAmount, creditTaxAmt,
                    description, number, dueDate, type, journalId
                ] = row;
            } else {
                let debitDept, creditDept;
                if (hasCreditCardColumn) {
                    [
                        flag, voucherNo, settlement, dateStr,
                        debitAccName, debitSub, debitDept, debitTax, debitAmount, debitTaxAmt,
                        creditAccName, creditSub, creditDept, creditTax, creditAmount, creditTaxAmt,
                        description, number, dueDate, type, journalId,
                        voucher, voucherUrl, voucherThumb, voucherId,
                        category, expenseCategory,
                        debitInvoiceNumber, debitVendorName, debitVendorMemo,
                        creditInvoiceNumber, creditVendorName, creditVendorMemo,
                        createdAt, creditCardId,
                        deletedAt, deletedBy, deletedByName
                    ] = row;
                } else {
                    [
                        flag, voucherNo, settlement, dateStr,
                        debitAccName, debitSub, debitDept, debitTax, debitAmount, debitTaxAmt,
                        creditAccName, creditSub, creditDept, creditTax, creditAmount, creditTaxAmt,
                        description, number, dueDate, type, journalId,
                        voucher, voucherUrl, voucherThumb, voucherId,
                        category, expenseCategory,
                        debitInvoiceNumber, debitVendorName, debitVendorMemo,
                        creditInvoiceNumber, creditVendorName, creditVendorMemo,
                        createdAt,
                        deletedAt, deletedBy, deletedByName
                    ] = row;
                }
            }

            if (flag !== '2000') {
                console.log('skip row', i, flag);
                continue;
            }

            const debitAcc = getOrCreateAccount(debitAccName);
            const creditAcc = getOrCreateAccount(creditAccName);
            const debitTaxParsed = parseTaxString(debitTax);
            const creditTaxParsed = parseTaxString(creditTax);

            const id = journalId || `${voucherNo}_${dateStr}`;

            const amount = debitAmount || creditAmount || '0';
            const importKey = `${dateStr}_${debitAccName || ''}_${creditAccName || ''}_${amount}_${description || ''}`;

            if (existingImportKeys.has(importKey)) {
                console.log('duplicate skip:', importKey);
                skippedCount++;
                continue;
            }

            if (!journalMap.has(id)) {
                // インポート判定: CSVにcreatedAtがない場合は外部からのインポート
                const isImportedEntry = !createdAt;
                const now = new Date().toISOString();
                const journalObj = {
                    id: id,
                    date: fromYayoiDate(dateStr),
                    description: description || '',
                    importKey: importKey,
                    lines: [],
                    voucher: voucher || null,
                    voucherUrl: voucherUrl || null,
                    voucherThumb: voucherThumb || null,
                    voucherId: voucherId || null,
                    category: category || '',
                    expenseCategory: expenseCategory || '',
                    createdAt: createdAt || null,
                    updatedAt: now,
                    isImported: isImportedEntry,
                    importedAt: isImportedEntry ? now : null
                };
                if (deletedAt) {
                    journalObj.deletedAt = deletedAt;
                    journalObj.deletedBy = deletedBy || '';
                    journalObj.deletedByName = deletedByName || '';
                }
                journalMap.set(id, journalObj);
            }

            const journal = journalMap.get(id);

            if (voucher && !journal.voucher) {
                journal.voucher = voucher;
                journal.voucherUrl = voucherUrl || null;
                journal.voucherThumb = voucherThumb || null;
                journal.voucherId = voucherId || null;
            }

            if (category && !journal.category) {
                journal.category = category;
            }
            if (expenseCategory && !journal.expenseCategory) {
                journal.expenseCategory = expenseCategory;
            }

            if (debitAcc && debitAmount) {
                journal.lines.push({
                    accountId: debitAcc.id,
                    type: 'debit',
                    amount: Number(debitAmount) || 0,
                    taxType: debitTaxParsed.taxType,
                    taxRate: debitTaxParsed.taxRate,
                    isQualified: debitTaxParsed.isQualified,
                    invoiceNumber: debitInvoiceNumber || '',
                    vendorName: debitVendorName || '',
                    vendorMemo: debitVendorMemo || ''
                });
            }

            if (creditAcc && creditAmount) {
                const creditLine = {
                    accountId: creditAcc.id,
                    type: 'credit',
                    amount: Number(creditAmount) || 0,
                    taxType: creditTaxParsed.taxType,
                    taxRate: creditTaxParsed.taxRate,
                    isQualified: creditTaxParsed.isQualified,
                    invoiceNumber: creditInvoiceNumber || '',
                    vendorName: creditVendorName || '',
                    vendorMemo: creditVendorMemo || ''
                };
                if (creditCardId) {
                    creditLine.creditCardId = creditCardId;
                }
                journal.lines.push(creditLine);
            }
        }

        const journals = Array.from(journalMap.values())
            .map(j => ({
                ...j,
                lines: j.lines.filter(line => line.accountId !== null)
            }))
            .filter(j => j.lines.length > 0);
        const newAccounts = Array.from(newAccountsMap.values());
        console.log('result:', journals.length, 'skipped:', skippedCount);

        return { journals, newAccounts, skippedCount };
    };

    const accountsToCSV = (accounts) => {
        const lines = [];
        lines.push(['id', 'code', 'name', 'kana', 'type', 'home', 'statement', 'counterpart', 'description'].map(escapeCSV).join(','));
        accounts.forEach(acc => {
            lines.push([
                acc.id || acc.code,  // UUIDがなければcodeを使用（後方互換性）
                acc.code,
                acc.name,
                acc.kana || '',
                TYPE_MAP_REVERSE[acc.type] || '資産',
                HOME_MAP_REVERSE[acc.home] || '借方',
                STATEMENT_MAP_REVERSE[acc.statement] || '貸借対照表',
                acc.counterpart || '',
                acc.description || ''
            ].map(escapeCSV).join(','));
        });
        return lines.join('\n');
    };

    const csvToAccounts = (csvText) => {
        const rows = parseCSV(csvText);
        if (rows.length < 2) return null;

        const firstRow = rows[0];
        const firstField = (firstRow[0] || '').trim();
        const rowText = firstRow.join(',');

        const hasIdentifier = /^(2000|2100|2110|2101|2111|識別フラグ)$/.test(firstField);
        const hasDatePattern = /\d{4}\/\d{1,2}\/\d{1,2}/.test(rowText);
        const hasJournalHeader = rowText.includes('借方科目') || rowText.includes('取引日付');

        if (hasIdentifier || hasDatePattern || hasJournalHeader) {
            return { error: 'journals' };
        }

        const header = rows[0].map(h => h.toLowerCase().trim());
        const getIndex = (name) => header.indexOf(name);

        // 弥生形式の検出
        const isYayoi = rows[0].includes('勘定科目名') && rows[0].includes('検索キー１');

        const accounts = [];
        for (let i = 1; i < rows.length; i++) {
            const row = rows[i];
            if (row.length < 2) continue;

            let id, code, name, kana, type, home, statement, counterpart, description;

            if (isYayoi) {
                // 弥生形式: 区分名, 勘定科目名, 検索キー１, 検索キー２, 貸借, 税区分, ...
                // 弥生形式にはUUIDがないので後でgenerateAccountIdで生成
                id = null;
                const category = row[0] || '';  // 区分名（例：【現金･預金】）
                name = row[1] || '';            // 勘定科目名
                code = row[2] || '';            // 検索キー１（科目コード）
                kana = row[3] || '';            // 検索キー２（読み仮名）
                const homeStr = row[4] || '';   // 貸借（借方/貸方）

                // 貸借から type を推定
                home = homeStr === '貸方' ? 'credit' : 'debit';

                // 区分名から type を推定
                if (category.includes('現金') || category.includes('預金') || category.includes('債権') ||
                    category.includes('有価証券') || category.includes('棚卸') || category.includes('資産')) {
                    type = ACCOUNT_TYPES.ASSET;
                    statement = 'BS';
                } else if (category.includes('債務') || category.includes('未払') || category.includes('借入')) {
                    type = ACCOUNT_TYPES.LIABILITY;
                    statement = 'BS';
                } else if (category.includes('資本') || category.includes('剰余') || category.includes('純資産')) {
                    type = ACCOUNT_TYPES.EQUITY;
                    statement = 'BS';
                } else if (category.includes('売上') || category.includes('収入') || category.includes('収益')) {
                    type = ACCOUNT_TYPES.REVENUE;
                    statement = 'PL';
                } else {
                    type = ACCOUNT_TYPES.EXPENSE;
                    statement = 'PL';
                }

                counterpart = '';
                description = '';
            } else {
                const idIdx = getIndex('id');
                const codeIdx = getIndex('code');
                const nameIdx = getIndex('name');

                if (codeIdx >= 0 && nameIdx >= 0) {
                    id = idIdx >= 0 ? (row[idIdx] || null) : null;
                    code = row[codeIdx] || '';
                    name = row[nameIdx] || '';
                    kana = row[getIndex('kana')] || '';
                    const typeStr = row[getIndex('type')] || '';
                    const homeStr = row[getIndex('home')] || '';
                    const stmtStr = row[getIndex('statement')] || '';
                    counterpart = row[getIndex('counterpart')] || '';
                    description = row[getIndex('description')] || '';

                    type = TYPE_MAP[typeStr] || ACCOUNT_TYPES.ASSET;
                    home = HOME_MAP[homeStr] || 'debit';
                    statement = STATEMENT_MAP[stmtStr] || 'BS';
                } else {
                    // 旧形式（idなし）
                    [code, name, kana, type, home, statement, counterpart, description] = row;
                    id = null;
                    type = TYPE_MAP[type] || type || ACCOUNT_TYPES.ASSET;
                    home = HOME_MAP[home] || home || 'debit';
                    statement = STATEMENT_MAP[statement] || statement || 'BS';
                }
            }

            if (!code || !name) continue;

            // idがない場合（弥生形式や旧形式）はcodeをidとして使用
            // 実際のインポート時にはgetOrCreateAccountで適切なUUIDが割り当てられる
            accounts.push({
                id: id || code,
                code,
                name,
                kana: (kana || '').toUpperCase(),
                type: type,
                home: home,
                statement: statement,
                counterpart: counterpart || '',
                description: description || ''
            });
        }
        return accounts.length > 0 ? accounts : null;
    };

    const saveData = async (year, data) => {
        if (!userEmail) {
            console.warn('User email is required to save data');
            return false;
        }
        try {
            const journalsCSV = journalsToCSV(data.journals, data.accounts);
            const deletedCSV = data.deletedJournals ? journalsToCSV(data.deletedJournals, data.accounts, { includeDeletedMeta: true }) : '';
            const response = await fetch(`${API_URL}?action=saveJournals&type=${getApiType()}&year=${year}&user=${encodeURIComponent(userEmail)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ journals: journalsCSV, deleted: deletedCSV })
            });
            const result = await response.json();
            return result.success;
        } catch (err) {
            console.error('Failed to save data:', err);
            return false;
        }
    };

    const loadData = async (year, accountsForParsing = null) => {
        if (!userEmail) {
            console.warn('User email is required to load data');
            return null;
        }
        try {
            // 保存済み科目をまず取得し、なければDEFAULT_ACCOUNTSを使用
            let accounts = accountsForParsing || DEFAULT_ACCOUNTS;
            try {
                const accResponse = await fetch(`${API_URL}?action=getAccounts&type=${getApiType()}&user=${encodeURIComponent(userEmail)}`);
                if (accResponse.ok) {
                    const accData = await accResponse.json();
                    if (accData.accounts && accData.accounts.trim()) {
                        const saved = csvToAccounts(accData.accounts);
                        if (saved && saved.length > 0) accounts = saved;
                    }
                }
            } catch (err) {
                console.warn('Failed to load saved accounts, using defaults:', err);
            }

            let journals = [];
            let deletedJournals = [];
            let newAccounts = [];

            try {
                const response = await fetch(`${API_URL}?action=getJournals&type=${getApiType()}&year=${year}&user=${encodeURIComponent(userEmail)}`);
                if (response.ok) {
                    const data = await response.json();
                    if (data.journals && data.journals.trim()) {
                        const result = csvToJournals(data.journals, accounts);
                        journals = result.journals;
                        newAccounts = result.newAccounts;
                        if (newAccounts.length > 0) {
                            accounts = [...accounts, ...newAccounts];
                        }
                    }
                    if (data.deleted && data.deleted.trim()) {
                        const deletedResult = csvToJournals(data.deleted, accounts);
                        deletedJournals = deletedResult.journals;
                    }
                }
            } catch (err) {
                console.error('Failed to load journals:', err);
            }

            return { accounts, journals, deletedJournals, newAccounts };
        } catch (err) {
            console.error('Failed to load data:', err);
            return null;
        }
    };

    const listYears = async () => {
        const defaultYear = { year: getCurrentFiscalYear(), endMonth: getApiType() === 'kojin' ? 12 : 3 };
        if (!userEmail) {
            return [defaultYear];
        }
        try {
            const response = await fetch(`${API_URL}?action=init&type=${getApiType()}&user=${encodeURIComponent(userEmail)}`);
            if (response.ok) {
                const data = await response.json();
                if (data.years && data.years.length > 0) {
                    return data.years.sort((a, b) => b.year - a.year);
                }
            }
        } catch (err) {
            console.error('Failed to list years:', err);
        }
        return [defaultYear];
    };

    const createYear = async (year, endMonth) => {
        if (!userEmail) return false;
        try {
            const response = await fetch(`${API_URL}?action=createYear&type=${getApiType()}&user=${encodeURIComponent(userEmail)}&year=${year}&endMonth=${endMonth}`);
            return response.ok;
        } catch (err) {
            console.error('Failed to create year:', err);
            return false;
        }
    };

    const changeFiscalMonth = async (year, newEndMonth) => {
        if (!userEmail) return false;
        try {
            const response = await fetch(`${API_URL}?action=changeFiscalMonth&type=${getApiType()}&user=${encodeURIComponent(userEmail)}&year=${year}&endMonth=${newEndMonth}`);
            return response.ok;
        } catch (err) {
            console.error('Failed to change fiscal month:', err);
            return false;
        }
    };

    const listVoucherFiles = async (year) => {
        try {
            const response = await fetch(`${API_URL}?action=listVouchers&type=${getApiType()}&user=${encodeURIComponent(userEmail)}&year=${year}`);
            if (response.ok) {
                const data = await response.json();
                return (data.vouchers || []).map(v => ({
                    name: v.name,
                    id: v.id,
                    viewUrl: v.webViewLink,
                    thumbnailUrl: v.thumbnailLink,
                    size: v.size,
                    createdTime: v.createdTime
                }));
            }
            return [];
        } catch (err) {
            console.error('Failed to list vouchers:', err);
            return [];
        }
    };

    const uploadVoucher = async (year, file) => {
        try {
            console.log('Uploading voucher:', file.name, 'size:', file.size, 'type:', file.type);
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch(`${API_URL}?action=uploadVoucher&type=${getApiType()}&user=${encodeURIComponent(userEmail)}&year=${year}`, {
                method: 'POST',
                body: formData
            });

            const data = await response.json();
            console.log('Upload response:', response.status, data);

            if (response.ok && data.file) {
                return {
                    name: data.file.name,
                    id: data.file.id,
                    viewUrl: data.file.webViewLink,
                    thumbnailUrl: data.file.thumbnailLink
                };
            }
            console.error('Upload failed:', data.error || 'Unknown error');
            return null;
        } catch (err) {
            console.error('Failed to upload voucher:', err);
            return null;
        }
    };

    const deleteVoucher = async (year, fileId) => {
        try {
            const response = await fetch(`${API_URL}?action=deleteVoucher&type=${getApiType()}&user=${encodeURIComponent(userEmail)}&year=${year}&fileId=${fileId}`);
            return response.ok;
        } catch (err) {
            console.error('Failed to delete voucher:', err);
            return false;
        }
    };

    const listDeletedVouchers = async (year) => {
        try {
            const response = await fetch(`${API_URL}?action=listDeletedVouchers&type=${getApiType()}&user=${encodeURIComponent(userEmail)}&year=${year}`);
            if (response.ok) {
                const data = await response.json();
                return data.files || [];
            }
            return [];
        } catch (err) {
            console.error('Failed to list deleted vouchers:', err);
            return [];
        }
    };

    const restoreVoucher = async (year, fileId) => {
        try {
            const response = await fetch(`${API_URL}?action=restoreVoucher&type=${getApiType()}&user=${encodeURIComponent(userEmail)}&year=${year}&fileId=${fileId}`);
            return response.ok;
        } catch (err) {
            console.error('Failed to restore voucher:', err);
            return false;
        }
    };

    const getCreditCards = async () => {
        if (!userEmail) {
            console.warn('User email is required to load credit cards');
            return [];
        }
        try {
            const response = await fetch(`${API_URL}?action=getCreditCards&type=${getApiType()}&user=${encodeURIComponent(userEmail)}`);
            if (!response.ok) {
                return [];
            }
            const data = await response.json();
            return data.creditCards || [];
        } catch (err) {
            console.error('Failed to load credit cards:', err);
            return [];
        }
    };

    const saveCreditCards = async (creditCards) => {
        if (!userEmail) {
            console.warn('User email is required to save credit cards');
            return false;
        }
        try {
            const response = await fetch(
                `${API_URL}?action=saveCreditCards&type=${getApiType()}&user=${encodeURIComponent(userEmail)}`,
                {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ creditCards })
                }
            );
            const result = await response.json();
            return result.success;
        } catch (err) {
            console.error('Failed to save credit cards:', err);
            return false;
        }
    };

    const getApportionment = async (year) => {
        if (!userEmail) return null;
        try {
            const response = await fetch(`${API_URL}?action=getApportionment&type=${getApiType()}&user=${encodeURIComponent(userEmail)}&year=${year}`);
            if (response.ok) {
                const data = await response.json();
                return data.apportionment;
            }
            return null;
        } catch (err) {
            console.error('Failed to get apportionment:', err);
            return null;
        }
    };

    const saveApportionment = async (year, apportionment) => {
        if (!userEmail) return false;
        try {
            const response = await fetch(`${API_URL}?action=saveApportionment&type=${getApiType()}&user=${encodeURIComponent(userEmail)}&year=${year}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ apportionment })
            });
            const result = await response.json();
            return result.success;
        } catch (err) {
            console.error('Failed to save apportionment:', err);
            return false;
        }
    };

    const getDeductions = async (year) => {
        if (!userEmail) return null;
        try {
            const response = await fetch(`${API_URL}?action=getDeductions&type=${getApiType()}&user=${encodeURIComponent(userEmail)}&year=${year}`);
            if (response.ok) {
                const data = await response.json();
                return data.deductions;
            }
            return null;
        } catch (err) {
            console.error('Failed to get deductions:', err);
            return null;
        }
    };

    const saveDeductions = async (year, deductions) => {
        if (!userEmail) return false;
        try {
            const response = await fetch(`${API_URL}?action=saveDeductions&type=${getApiType()}&user=${encodeURIComponent(userEmail)}&year=${year}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ deductions })
            });
            const result = await response.json();
            return result.success;
        } catch (err) {
            console.error('Failed to save deductions:', err);
            return false;
        }
    };

    // メンバー管理
    const getAccessibleBooks = async (authEmail) => {
        try {
            const response = await fetch(`${API_URL}?action=getAccessibleBooks&user=${encodeURIComponent(authEmail)}`);
            const data = await response.json();
            return data.books || [];
        } catch (err) {
            console.error('Failed to get accessible books:', err);
            return [];
        }
    };

    const getMembers = async () => {
        try {
            const response = await fetch(`${API_URL}?action=getMembers&user=${encodeURIComponent(userEmail)}`);
            const data = await response.json();
            return data.members || [];
        } catch (err) {
            console.error('Failed to get members:', err);
            return [];
        }
    };

    const inviteMember = async (memberEmail) => {
        try {
            const response = await fetch(`${API_URL}?action=inviteMember&user=${encodeURIComponent(userEmail)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ memberEmail })
            });
            const data = await response.json();
            if (!data.success) throw new Error(data.error);
            return { orgName: data.orgName };
        } catch (err) {
            console.error('Failed to invite member:', err);
            throw err;
        }
    };

    const acceptInvite = async (token, email) => {
        try {
            const response = await fetch(`${API_URL}?action=acceptInvite`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, email })
            });
            const data = await response.json();
            if (!data.success) throw new Error(data.error);
            return { ownerEmail: data.ownerEmail, orgInfo: data.orgInfo };
        } catch (err) {
            console.error('Failed to accept invite:', err);
            throw err;
        }
    };

    const getInvitations = async () => {
        try {
            const response = await fetch(`${API_URL}?action=getInvitations&user=${encodeURIComponent(userEmail)}`);
            const data = await response.json();
            return data.invitations || [];
        } catch (err) {
            console.error('Failed to get invitations:', err);
            return [];
        }
    };

    const cancelInvite = async (token) => {
        try {
            const response = await fetch(`${API_URL}?action=cancelInvite&user=${encodeURIComponent(userEmail)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token })
            });
            const data = await response.json();
            if (!data.success) throw new Error(data.error);
            return true;
        } catch (err) {
            console.error('Failed to cancel invite:', err);
            throw err;
        }
    };

    const removeMember = async (memberEmail) => {
        try {
            const response = await fetch(`${API_URL}?action=removeMember&user=${encodeURIComponent(userEmail)}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ memberEmail })
            });
            const data = await response.json();
            if (!data.success) throw new Error(data.error);
            return true;
        } catch (err) {
            console.error('Failed to remove member:', err);
            throw err;
        }
    };

    return {
        isConnected,
        basePath,
        selectBaseDirectory,
        saveData,
        loadData,
        listYears,
        createYear,
        changeFiscalMonth,
        listVoucherFiles,
        uploadVoucher,
        deleteVoucher,
        listDeletedVouchers,
        restoreVoucher,
        getYearDirectory,
        getSettings,
        saveSettings,
        accountsToCSV,
        journalsToCSV,
        csvToJournals,
        csvToAccounts,
        getCreditCards,
        saveCreditCards,
        getApportionment,
        saveApportionment,
        getDeductions,
        saveDeductions,
        getAccessibleBooks,
        getMembers,
        inviteMember,
        acceptInvite,
        getInvitations,
        cancelInvite,
        removeMember
    };
};

export default useFileSystem;
